import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save } from "lucide-react";

const STU_STATUS = ["Active", "Graduated", "Alumni", "Discontinued"];

const empty = {
  student_name: "", personal_email: "", phone: "", student_id: "",
  course: "", department: "", admission_year: "", graduation_year: "",
  batch: "", student_status: "Active",
  date_of_birth: "", pan_number: "",
};

export default function AddStudentPage() {
  const nav = useNavigate();
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...f,
        admission_year: Number(f.admission_year),
        graduation_year: Number(f.graduation_year),
        date_of_birth: f.date_of_birth || null,
        pan_number: f.pan_number || null,
      };
      const { data } = await api.post("/records/students", payload);
      if (data.status === "Linked") {
        toast.success("Student added and linked to existing EthicCred passport.");
      } else {
        toast.success("Student added. Awaiting individual to claim.");
      }
      nav("/workspace/students");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceLayout subtitle="Institution workspace" title="Add Student">
      <Card className="rounded-sm border-ec-line">
        <CardContent className="p-6">
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="add-student-form">
            <Field label="Student Name" required>
              <Input value={f.student_name} onChange={(e) => setF({ ...f, student_name: e.target.value })} required data-testid="stu-name" />
            </Field>
            <Field label="Personal Email" required>
              <Input type="email" value={f.personal_email} onChange={(e) => setF({ ...f, personal_email: e.target.value })} required data-testid="stu-email" />
            </Field>
            <Field label="Phone Number" required>
              <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required data-testid="stu-phone" />
            </Field>
            <Field label="Student ID" required>
              <Input value={f.student_id} onChange={(e) => setF({ ...f, student_id: e.target.value })} required data-testid="stu-id" />
            </Field>
            <Field label="Course" required>
              <Input value={f.course} onChange={(e) => setF({ ...f, course: e.target.value })} required data-testid="stu-course" />
            </Field>
            <Field label="Department" required>
              <Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} required data-testid="stu-department" />
            </Field>
            <Field label="Admission Year" required>
              <Input type="number" min={1990} max={2100} value={f.admission_year} onChange={(e) => setF({ ...f, admission_year: e.target.value })} required data-testid="stu-admission-year" />
            </Field>
            <Field label="Graduation Year" required>
              <Input type="number" min={1990} max={2100} value={f.graduation_year} onChange={(e) => setF({ ...f, graduation_year: e.target.value })} required data-testid="stu-graduation-year" />
            </Field>
            <Field label="Batch" required>
              <Input value={f.batch} onChange={(e) => setF({ ...f, batch: e.target.value })} placeholder="e.g. B-Tech-2024" required data-testid="stu-batch" />
            </Field>
            <Field label="Student Status" required>
              <Select value={f.student_status} onValueChange={(v) => setF({ ...f, student_status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="stu-status-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{STU_STATUS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <div className="sm:col-span-2 border-t border-ec-line pt-5">
              <div className="eyebrow mb-3">Optional fields</div>
            </div>
            <Field label="Date of Birth">
              <Input type="date" value={f.date_of_birth} onChange={(e) => setF({ ...f, date_of_birth: e.target.value })} data-testid="stu-dob" />
            </Field>
            <Field label="PAN Number" hint="Optional · Encrypted · Masked (ABCDE****F)">
              <Input value={f.pan_number} onChange={(e) => setF({ ...f, pan_number: e.target.value.toUpperCase() })} maxLength={10} placeholder="ABCDE1234F" data-testid="stu-pan" />
            </Field>

            <div className="sm:col-span-2 flex justify-end gap-2 border-t border-ec-line pt-5">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => nav("/workspace/students")}>Cancel</Button>
              <Button type="submit" disabled={loading} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="stu-submit">
                <Save className="w-4 h-4" /> {loading ? "Saving…" : "Add Student"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </WorkspaceLayout>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <Label className="eyebrow">{label} {required && <span className="text-ec-emerald">*</span>}</Label>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-[11px] text-ec-ink-soft mt-1 font-mono-plex">{hint}</p>}
    </div>
  );
}
